import { Page } from "../../components/layout/Page";
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  Flex,
  Menu,
  Popover,
  Select,
  Skeleton,
  Table,
  Text,
} from "@mantine/core";
import { serverOptionsFilter } from "@dehub/theme";
import {
  CaretDownIcon,
  CaretUpDownIcon,
  CaretUpIcon,
  DownloadSimpleIcon,
  PlusIcon,
  ReceiptIcon,
  XIcon,
} from "@phosphor-icons/react";
import dayjs from "dayjs";
import qs from "qs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import useSwr from "swr";
import { InvoiceApi, NetworkApi } from "../../apis";
import { ReminderApi } from "../../apis/reminder";
import {
  BulkActionBar,
  DateRangeChip,
  DueCell,
  ListFooter,
  ListPageHeader,
  ListToolbar,
  MoneyCell,
  PartyCell,
  PaymentProgressCell,
  RowActions,
  ListStates, resolveListStates,
  StatCards,
} from "../../components/list";
import sharedListClasses from "../../components/list/list.module.css";
import { usePermission } from "../../components/permission";
import { ReasonTooltip } from "../../components/ui/reason-tooltip";
import {
  RegisterPayment,
  REGISTER_PAYMENT_DRAWER_STYLES,
} from "../../components/register-payment/register-payment";
import { ITableRef } from "../../components/data-table";
import { StatusBadge, resolveInvoiceStatusColor } from "../../components/ui/status-badge"
import modalChrome from "../../components/ui/app-modal.module.css";
import { useDebouncedSearch } from "../../hooks/useDebouncedSearch";
import { usePdfDownload } from "../../hooks/usePdfDownload";
import { useRowSelection } from "../../hooks/useRowSelection";
import { IAuth } from "../../interfaces/IAuth";
import { IGeneral } from "../../interfaces/IGeneral";
import { IReference } from "../../interfaces/IReference";
import { currencyFormat } from "../../utils";
import { message } from "../../utils/Message";
import { SELECT_ALL_CAP, saveInvoicePdfs } from "../../utils/bulk-invoice";
import { errorReason } from "../../utils/error-handler";
import { renderBusinessName } from "../../utils/render-business-name";
import listClasses from "./List.module.css";

// CLOSED has its own page; RETURNED never reaches this list. CANCELED does
// (cancelled orders cancel their invoices), so it keeps a tab.
const EXCLUDED_STATUSES = ["CLOSED", "RETURNED"];
// Canvas tab order; codes not listed keep their reference order after these.
const STATUS_TAB_ORDER = ["CONFIRMED", "SENT", "DRAFT", "REJECTED", "CANCELED"];
const statusTabRank = (code: string) => {
  const i = STATUS_TAB_ORDER.indexOf(code);
  return i === -1 ? STATUS_TAB_ORDER.length : i;
};

type SortField = "confirmedDate" | "paymentDate" | "confirmedAmount" | "amountToPay";
type SortDir = "asc" | "desc";
type DueFilter = "overdue" | "week" | "none" | null;

type ListQuery = {
  status: string;
  query: string;
  receiverBusinessId: string | null;
  confirmedFrom: string | null;
  confirmedTo: string | null;
  due: DueFilter;
  sortField: SortField | null;
  sortDir: SortDir;
  page: number;
  limit: number;
};

type SentSummary = {
  /** Every row the filter lists — the tab badge. */
  count: number;
  /** CONFIRMED invoices behind the money KPIs; absent on older backends. */
  receivableCount?: number;
  confirmedAmount: number;
  /** Present on new backends; falls back to confirmedAmount when absent. */
  invoicedAmount?: number;
  paidAmount: number;
  amountToPay: number;
  overdueCount: number;
  overdueAmount: number;
};

/** Compact MNT for KPI: million / billion, 2 decimals. Full value via tooltip. */
function formatCompactMnt(
  amount: number,
  t: (key: string) => string,
): { value: string; unit: string; full: string } {
  const full = currencyFormat(amount, { withSymbol: true });
  const abs = Math.abs(amount);
  if (abs >= 1_000_000_000) {
    return {
      value: (amount / 1_000_000_000).toFixed(2),
      unit: t("invoice:summaryStrip.unitBillion"),
      full,
    };
  }
  return {
    value: (amount / 1_000_000).toFixed(2),
    unit: t("invoice:summaryStrip.unitMillion"),
    full,
  };
}

/**
 * KPI percentage display:
 * 0 → "0"; 0<x<1 → one decimal; 1≤x<99.5 → whole; 99.5≤x<100 → one decimal, floored (never "100"); 100 → "100".
 * ratio is capped at 100 for progress bars.
 */
function formatKpiPercent(part: number, whole: number): { ratio: number; pct: string } {
  if (!(whole > 0) || !Number.isFinite(part) || part <= 0) {
    return { ratio: 0, pct: "0" };
  }
  const raw = Math.min(100, (part / whole) * 100);
  if (raw <= 0) return { ratio: 0, pct: "0" };
  // Floor, not round: 0.96 must not read "1.0" and 99.96 must not read "100".
  if (raw < 1) return { ratio: raw, pct: (Math.floor(raw * 10) / 10).toFixed(1) };
  if (raw < 99.5) return { ratio: raw, pct: String(Math.round(raw)) };
  if (raw < 100) return { ratio: raw, pct: (Math.floor(raw * 10) / 10).toFixed(1) };
  return { ratio: 100, pct: "100" };
}

function parseDue(raw: string | null): DueFilter {
  if (raw === "overdue" || raw === "week" || raw === "none") return raw;
  return null;
}

function parseSortField(raw: string | null): SortField | null {
  if (
    raw === "confirmedDate" ||
    raw === "paymentDate" ||
    raw === "confirmedAmount" ||
    raw === "amountToPay"
  ) {
    return raw;
  }
  return null;
}

function readQuery(params: URLSearchParams): ListQuery {
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);
  const limit = Math.max(1, parseInt(params.get("limit") || "20", 10) || 20);
  return {
    status: params.get("status") || "",
    query: params.get("query") || "",
    receiverBusinessId: params.get("partner") || null,
    confirmedFrom: params.get("from") || null,
    confirmedTo: params.get("to") || null,
    due: parseDue(params.get("due")),
    sortField: parseSortField(params.get("sort")),
    sortDir: params.get("dir") === "asc" ? "asc" : "desc",
    page,
    limit,
  };
}

function writeQuery(q: ListQuery): URLSearchParams {
  const p = new URLSearchParams();
  if (q.status) p.set("status", q.status);
  if (q.query) p.set("query", q.query);
  if (q.receiverBusinessId) p.set("partner", q.receiverBusinessId);
  if (q.confirmedFrom) p.set("from", q.confirmedFrom);
  if (q.confirmedTo) p.set("to", q.confirmedTo);
  if (q.due) p.set("due", q.due);
  if (q.sortField) {
    p.set("sort", q.sortField);
    p.set("dir", q.sortDir);
  }
  if (q.page !== 1) p.set("page", String(q.page));
  if (q.limit !== 20) p.set("limit", String(q.limit));
  return p;
}

export const SalesInvoiceList = () => {
  const { t } = useTranslation(["invoice", "common", "settings"]);

  const invoiceStatus = useSelector((state: { general: IGeneral }) => state.general.invoiceStatus);
  const invoicePaymentStatus = useSelector(
    (state: { general: IGeneral }) => state.general.invoicePaymentStatus,
  );
  const myBusinessType = useSelector((state: { auth: IAuth }) => state.auth.myBusinessType);
  const [action, setAction] = useState<any[]>([]);
  const [bulkBusy, setBulkBusy] = useState<"pdf" | "reminder" | "selectAll" | null>(null);
  const [partnerLabel, setPartnerLabel] = useState<string | null>(null);
  const [partnerOpen, setPartnerOpen] = useState(false);
  const tableRef = useRef<ITableRef>(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const { download: downloadPdf, isLoading: isPdfLoading } = usePdfDownload({ fetcher: InvoiceApi.pdf });

  const INV_LIST = usePermission("INV_LIST");
  const INV_CREATE = usePermission("INV_CREATE");
  const INV_REG_PAYMENT = usePermission("INV_REG_PAYMENT");
  const INV_PDF = usePermission("INV_PDF");
  const INV_EDIT_DRAFT = usePermission("INV_EDIT_DRAFT");
  const INV_SETT_SEND = usePermission("INV_SETT_SEND");

  const q = useMemo(() => readQuery(searchParams), [searchParams]);

  const patchQuery = useCallback(
    (patch: Partial<ListQuery>, resetPage = true) => {
      const next: ListQuery = {
        ...q,
        ...patch,
        ...(resetPage ? { page: 1 } : {}),
      };
      setSearchParams(writeQuery(next), { replace: true });
    },
    [q, setSearchParams],
  );

  const apiFilter = useMemo(
    () => ({
      query: q.query || null,
      status: q.status || null,
      receiverBusinessId: q.receiverBusinessId,
      confirmedFrom: q.confirmedFrom,
      confirmedTo: q.confirmedTo,
      due: q.due,
    }),
    [q],
  );

  const offset = useMemo(() => ({ page: q.page, limit: q.limit }), [q.page, q.limit]);

  const sort = useMemo(
    () =>
      q.sortField
        ? { field: q.sortField, dir: q.sortDir }
        : null,
    [q.sortField, q.sortDir],
  );

  const swrKey = INV_LIST.hasAccess
    ? `inv.invoice.sent?${qs.stringify({ offset, filter: apiFilter, sort })}`
    : null;

  const { data, error, isLoading, mutate } = useSwr<any>(
    swrKey,
    async () => await InvoiceApi.sent({ offset, filter: apiFilter, sort } as any),
  );

  const rows: any[] = useMemo(() => (data?.rows as any[]) ?? [], [data]);
  const total = data?.count ?? 0;
  const hasFilters = !!(q.query || q.status || q.receiverBusinessId || q.confirmedFrom || q.confirmedTo || q.due);
  const summary: SentSummary | null = data?.summary ?? null;

  const {
    query: partnerSearch,
    setQuery: setPartnerSearch,
    data: partnerSelects,
  } = useDebouncedSearch<any>({
    key: "inv.sent.partner.select",
    fetcher: (query) => NetworkApi.select({ query }),
    enabled: partnerOpen || !!q.receiverBusinessId,
  });

  useEffect(() => {
    if (!q.receiverBusinessId) {
      setPartnerLabel(null);
      return;
    }
    const found = (partnerSelects ?? []).find((b: any) => b?.id === q.receiverBusinessId);
    if (found) setPartnerLabel(renderBusinessName(found, "-"));
  }, [q.receiverBusinessId, partnerSelects]);

  const kpiDash = t("common:list.dash");
  const invoicedBase = summary
    ? Number(summary.invoicedAmount ?? summary.confirmedAmount ?? 0)
    : 0;
  const kpiInvoiced = summary ? formatCompactMnt(invoicedBase, t) : null;
  const kpiPaid = summary ? formatCompactMnt(summary.paidAmount, t) : null;
  const kpiRemaining = summary ? formatCompactMnt(summary.amountToPay, t) : null;
  const paidPct = summary
    ? formatKpiPercent(summary.paidAmount, invoicedBase)
    : { ratio: 0, pct: "0" };
  const remainingPct = summary
    ? formatKpiPercent(summary.amountToPay, invoicedBase)
    : { ratio: 0, pct: "0" };
  const overduePct = summary
    ? formatKpiPercent(summary.overdueAmount, summary.amountToPay)
    : { ratio: 0, pct: "0" };

  const hasChipFilters = !!(
    q.confirmedFrom ||
    q.confirmedTo ||
    q.receiverBusinessId ||
    q.due
  );

  // Selection survives paging (rows are kept, so the total covers every page);
  // a filter, tab or search change clears it.
  const selection = useRowSelection<any>(rows);
  const { selectedRows, allPageSelected, somePageSelected, clear: clearSelection } = selection;

  const selectedRemaining = useMemo(
    () => selectedRows.reduce((s, x) => s + Number(x?.amountToPay || 0), 0),
    [selectedRows],
  );

  const selectAllCount = Math.min(total, SELECT_ALL_CAP);
  const canSelectAll = allPageSelected && total > rows.length && selection.count < selectAllCount;

  const onSort = (field: SortField) => {
    if (q.sortField === field) {
      patchQuery({ sortDir: q.sortDir === "asc" ? "desc" : "asc" }, false);
    } else {
      patchQuery({ sortField: field, sortDir: "desc" });
    }
  };

  const sortIcon = (field: SortField) => {
    if (q.sortField !== field) return <CaretUpDownIcon size={11} opacity={0.35} />;
    return q.sortDir === "asc" ? (
      <CaretUpIcon size={11} weight="bold" />
    ) : (
      <CaretDownIcon size={11} weight="bold" />
    );
  };

  const clearChipFilters = () => {
    patchQuery({
      confirmedFrom: null,
      confirmedTo: null,
      receiverBusinessId: null,
      due: null,
    });
    setPartnerLabel(null);
    clearSelection();
  };

  const listStates = resolveListStates(
    <ListStates
      loading={isLoading}
      error={error}
      onRetry={() => mutate()}
      empty={rows.length === 0}
      filtered={hasFilters}
      query={q.query}
      onClear={() => {
        clearChipFilters();
        patchQuery({ query: "", status: "" });
      }}
      columns={8}
    />
  );

  const statusTabs = useMemo(
    () => [
      {
        value: "",
        label: t("common:all"),
        count: summary?.count != null ? summary.count : total > 0 ? total : undefined,
      },
      ...(invoiceStatus ?? [])
        .filter((s: IReference) => !EXCLUDED_STATUSES.includes(s.code))
        .sort((a: IReference, b: IReference) => statusTabRank(a.code) - statusTabRank(b.code))
        .map((s: IReference) => ({
          value: s.code,
          label: t(`invoice:invoiceStatus.${s.code}`, { defaultValue: s.name ?? "-" }),
        })),
    ],
    [invoiceStatus, t, total, summary],
  );

  // A draft opens straight in the editor; everything else opens its details.
  const rowHref = (inv: any): string | null => {
    if (!inv?.id) return null;
    if (inv.invoiceStatus === "DRAFT" && INV_EDIT_DRAFT.hasAccess) return `/sales-invoice/${inv.id}`;
    return `/sales-invoice/${inv.id}/details`;
  };

  const openRow = (inv: any) => {
    const href = rowHref(inv);
    if (href) navigate(href);
  };

  const handleSelectAll = async () => {
    setBulkBusy("selectAll");
    try {
      const res: any = await InvoiceApi.sent({
        offset: { page: 1, limit: selectAllCount },
        filter: apiFilter,
        sort,
      } as any);
      selection.select((res?.rows as any[]) ?? []);
    } catch (err) {
      message.error(errorReason(err));
    } finally {
      setBulkBusy(null);
    }
  };

  const handleBulkPdf = async () => {
    setBulkBusy("pdf");
    try {
      const failed = await saveInvoicePdfs(selectedRows);
      if (failed > 0) message.error(t("invoice:bulk.pdfFailed", { failed, count: selectedRows.length }));
    } finally {
      setBulkBusy(null);
    }
  };

  const handleBulkReminder = async () => {
    setBulkBusy("reminder");
    let sent = 0;
    const reasons = new Map<string, number>();
    try {
      for (const row of selectedRows) {
        try {
          const res: any = await ReminderApi.sendNow(row.id);
          // send-now answers 200 with status FAILED when the notify call fails; `error` says why.
          if (res?.status === "FAILED") throw new Error(res.error || t("settings:reminderDrawer.sendError"));
          sent += 1;
        } catch (err) {
          const reason = errorReason(err);
          reasons.set(reason, (reasons.get(reason) ?? 0) + 1);
        }
      }
    } finally {
      setBulkBusy(null);
    }

    if (reasons.size === 0) {
      message.success(t("invoice:bulk.reminderSent", { count: sent }));
      clearSelection();
      return;
    }
    const detail = Array.from(reasons, ([reason, n]) => `${reason} (${n})`).join("; ");
    message.error(`${t("invoice:bulk.reminderPartial", { sent, failed: selectedRows.length - sent })} ${detail}`);
  };

  const partySubline = (inv: any) => {
    const ref = (inv?.refCode ?? "").toString().trim();
    const confirmed = inv?.confirmedDate ?? inv?.sentDate;
    const datePart = confirmed ? dayjs(confirmed).format("MM-DD") : "";
    if (ref && datePart) return `${ref} · ${datePart}`;
    return ref || datePart || null;
  };

  const dueLabel =
    q.due === "overdue"
      ? t("invoice:chips.dueOverdue")
      : q.due === "week"
        ? t("invoice:chips.dueWeek")
        : q.due === "none"
          ? t("invoice:chips.dueNone")
          : null;

  return (
    <Page>
      <ListPageHeader
        eyebrow={t("invoice:breadcrumb.salesInvoice")}
        title={t("invoice:openInvoice")}
        search={{
          value: q.query,
          onChange: (v) => {
            patchQuery({ query: v });
            clearSelection();
          },
          placeholder: t("invoice:searchPlaceholder"),
        }}
        secondaryActions={
          <ReasonTooltip reason={INV_REG_PAYMENT.reason}>
            <Button
              className={sharedListClasses.headerBtnSecondary}
              leftSection={<ReceiptIcon size={16} />}
              onClick={() => setAction(["payment_register"])}
            >
              {t("invoice:registerPayment")}
            </Button>
          </ReasonTooltip>
        }
        primaryAction={
          myBusinessType === "SUPPLIER"
            ? {
                label: t("invoice:newInvoice"),
                onClick: () => navigate("/sales-invoice/new"),
                leftSection: <PlusIcon size={16} weight="bold" />,
                disabledReason: INV_CREATE.reason ?? INV_LIST.reason,
              }
            : undefined
        }
      />

      <StatCards
        items={[
          {
            key: "invoiced",
            label: t("invoice:summaryStrip.invoiced"),
            value: kpiInvoiced?.value ?? kpiDash,
            unit: kpiInvoiced?.unit,
            fullValue: kpiInvoiced?.full,
            tone: "gray",
            progress: summary ? 100 : 0,
            hint: summary
              ? t("invoice:summaryStrip.invoiceCount", { count: summary.receivableCount ?? summary.count })
              : "\u00a0",
          },
          {
            key: "paid",
            label: t("invoice:summaryStrip.paid"),
            value: kpiPaid?.value ?? kpiDash,
            unit: kpiPaid?.unit,
            fullValue: kpiPaid?.full,
            hint: summary
              ? t("invoice:summaryStrip.paidOfInvoiced", { pct: paidPct.pct })
              : "\u00a0",
            tone: "green",
            progress: paidPct.ratio,
          },
          {
            key: "remaining",
            label: t("invoice:summaryStrip.remaining"),
            value: kpiRemaining?.value ?? kpiDash,
            unit: kpiRemaining?.unit,
            fullValue: kpiRemaining?.full,
            tone: "dark",
            progress: remainingPct.ratio,
            hint: summary
              ? t("invoice:summaryStrip.remainingOfInvoiced", { pct: remainingPct.pct })
              : "\u00a0",
          },
          {
            key: "overdue",
            label: t("invoice:summaryStrip.overdue"),
            value:
              summary != null
                ? `${summary.overdueCount} ${t("invoice:summaryStrip.invoiceUnit")}`
                : kpiDash,
            fullValue:
              summary != null
                ? currencyFormat(summary.overdueAmount, { withSymbol: true })
                : undefined,
            tone: "red",
            progress: overduePct.ratio,
            hint:
              summary != null
                ? currencyFormat(summary.overdueAmount, { withSymbol: true })
                : "\u00a0",
            onClick: () => {
              patchQuery({ due: "overdue" });
              clearSelection();
            },
          },
        ]}
      />

      <Box className={listClasses.tableCard}>
        <ListToolbar
          tabs={statusTabs}
          activeTab={q.status}
          onTabChange={(value) => {
            patchQuery({ status: value });
            clearSelection();
          }}
          filterSlot={
            <>
              <DateRangeChip
                label={t("invoice:chips.confirmedDate")}
                value={[q.confirmedFrom, q.confirmedTo]}
                onChange={([confirmedFrom, confirmedTo]) => {
                  patchQuery({ confirmedFrom, confirmedTo });
                  clearSelection();
                }}
              />

              <Popover
                opened={partnerOpen}
                onChange={setPartnerOpen}
                position="bottom-end"
                shadow="md"
                withinPortal
              >
                <Popover.Target>
                  <Box
                    component="button"
                    type="button"
                    className={`${sharedListClasses.filterChip}${
                      q.receiverBusinessId ? ` ${sharedListClasses.filterChipActive}` : ""
                    }`}
                    onClick={() => setPartnerOpen((o) => !o)}
                  >
                    {q.receiverBusinessId ? (
                      <>
                        <Text span size="sm" fw={500}>
                          {partnerLabel ?? t("invoice:chips.partner")}
                        </Text>
                        <XIcon
                          size={12}
                          weight="bold"
                          onClick={(e) => {
                            e.stopPropagation();
                            patchQuery({ receiverBusinessId: null });
                            setPartnerLabel(null);
                            setPartnerOpen(false);
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <PlusIcon size={12} weight="bold" />
                        <Text span size="sm">
                          {t("invoice:chips.partner")}
                        </Text>
                      </>
                    )}
                  </Box>
                </Popover.Target>
                <Popover.Dropdown>
                  <Select
                    searchable
                    clearable
                    filter={serverOptionsFilter}
                    searchValue={partnerSearch}
                    onSearchChange={setPartnerSearch}
                    nothingFoundMessage={t("common:noResultsShort")}
                    data={(partnerSelects ?? []).map((item: any) => ({
                      label: renderBusinessName(item, ""),
                      value: item?.id ?? "",
                    }))}
                    value={q.receiverBusinessId}
                    onChange={(id) => {
                      const found = (partnerSelects ?? []).find((b: any) => b?.id === id);
                      setPartnerLabel(found ? renderBusinessName(found, "-") : null);
                      patchQuery({ receiverBusinessId: id });
                      setPartnerOpen(false);
                    }}
                    placeholder={t("invoice:chips.partner")}
                    w={280}
                  />
                </Popover.Dropdown>
              </Popover>

              <Menu shadow="md" width={200} position="bottom-end" withinPortal>
                <Menu.Target>
                  <Box
                    component="button"
                    type="button"
                    className={`${sharedListClasses.filterChip}${
                      dueLabel ? ` ${sharedListClasses.filterChipActive}` : ""
                    }`}
                  >
                    {dueLabel ? (
                      <>
                        <Text span size="sm" fw={500}>
                          {dueLabel}
                        </Text>
                        <XIcon
                          size={12}
                          weight="bold"
                          onClick={(e) => {
                            e.stopPropagation();
                            patchQuery({ due: null });
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <PlusIcon size={12} weight="bold" />
                        <Text span size="sm">
                          {t("invoice:chips.due")}
                        </Text>
                      </>
                    )}
                  </Box>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    onClick={() => {
                      patchQuery({ due: "overdue" });
                      clearSelection();
                    }}
                  >
                    {t("invoice:chips.dueOverdue")}
                  </Menu.Item>
                  <Menu.Item
                    onClick={() => {
                      patchQuery({ due: "week" });
                      clearSelection();
                    }}
                  >
                    {t("invoice:chips.dueWeek")}
                  </Menu.Item>
                  <Menu.Item
                    onClick={() => {
                      patchQuery({ due: "none" });
                      clearSelection();
                    }}
                  >
                    {t("invoice:chips.dueNone")}
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>

              {hasChipFilters ? (
                <Button variant="subtle" color="gray" size="compact-sm" onClick={clearChipFilters}>
                  {t("common:list.clearFilters")}
                </Button>
              ) : null}
            </>
          }
        />

        {!INV_LIST.hasAccess ? (
          INV_LIST.accessDenied()
        ) : (
          <Box className={listClasses.tblWrap}>
            {listStates ?? (
            <Table.ScrollContainer minWidth={1100}>
            <Table
              className={listClasses.tbl}
              withRowBorders={false}
              horizontalSpacing={0}
              verticalSpacing={0}
            >
              <Table.Thead>
                <Table.Tr className={sharedListClasses.scrollRow}>
                  <Table.Th className={`${listClasses.colCheck} ${sharedListClasses.stickyCheck}`}>
                    <Checkbox
                      color="brand"
                      size="sm"
                      radius="sm"
                      checked={allPageSelected}
                      indeterminate={somePageSelected && !allPageSelected}
                      onChange={selection.toggleAll}
                      aria-label={t("common:all")}
                    />
                  </Table.Th>
                  <Table.Th
                    className={`${listClasses.colActions} ${listClasses.colActionsSingle} ${sharedListClasses.stickyActions} ${sharedListClasses.stickyEdge}`}
                  >
                    <Text className={sharedListClasses.stickyActionsHead}>{t("common:actions")}</Text>
                  </Table.Th>
                  <Table.Th className={listClasses.sortable} onClick={() => onSort("confirmedDate")}>
                    <Flex align="center" gap={4} justify="flex-start">
                      {t("invoice:col.invoice")}
                      {sortIcon("confirmedDate")}
                    </Flex>
                  </Table.Th>
                  <Table.Th>{t("invoice:col.status")}</Table.Th>
                  <Table.Th className={listClasses.sortable} onClick={() => onSort("paymentDate")}>
                    <Flex align="center" gap={4} justify="flex-start">
                      {t("invoice:col.dueDate")}
                      {sortIcon("paymentDate")}
                    </Flex>
                  </Table.Th>
                  <Table.Th
                    className={`${listClasses.colNum} ${listClasses.sortable}`}
                    onClick={() => onSort("confirmedAmount")}
                  >
                    <Flex align="center" gap={4} justify="flex-end">
                      {t("invoice:col.amount")}
                      {sortIcon("confirmedAmount")}
                    </Flex>
                  </Table.Th>
                  <Table.Th className={listClasses.colPayment}>{t("invoice:col.paymentProgress")}</Table.Th>
                  <Table.Th
                    className={`${listClasses.colNum} ${listClasses.sortable}`}
                    onClick={() => onSort("amountToPay")}
                  >
                    <Flex align="center" gap={4} justify="flex-end">
                      {t("invoice:col.remaining")}
                      {sortIcon("amountToPay")}
                    </Flex>
                  </Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {rows.map((inv: any, i: number) => {
                        const status = invoiceStatus.find((x: IReference) => x.code === inv.invoiceStatus);
                        const payStatus = invoicePaymentStatus.find(
                          (x: IReference) => x.code === inv.paymentStatus,
                        );
                        const name = renderBusinessName(inv?.receiverBusiness, "-");
                        const isPaid = inv?.paymentStatus === "CLOSED";
                        const confirmedAmt = Number(inv?.confirmedAmount || 0);
                        const fallbackAmt = Number(inv?.totalAmount || 0);
                        const displayAmt = confirmedAmt > 0 ? confirmedAmt : fallbackAmt > 0 ? fallbackAmt : null;
                        const paidAmt = Number(inv?.paidAmount || 0);
                        const remainingAmt = Number(
                          inv?.amountToPay ?? Math.max(0, (displayAmt ?? 0) - paidAmt),
                        );
                        // A fully paid (CLOSED) invoice shows its payment status; a part-paid
                        // (DIVIDED) one keeps its invoice status, the progress bar shows the part.
                        const paidLike = inv?.paymentStatus === "CLOSED";
                        const statusCode = paidLike ? inv.paymentStatus : inv.invoiceStatus;
                        const label = paidLike
                          ? t(`invoice:paymentStatus.${inv.paymentStatus}`, { defaultValue: payStatus?.name ?? inv.paymentStatus })
                          : (status?.name ?? inv.invoiceStatus);
                        const selected = selection.isSelected(inv?.id);

                        return (
                          <Table.Tr
                            key={inv.id ?? inv.refCode ?? i}
                            className={sharedListClasses.scrollRow}
                            data-selected={selected || undefined}
                            onClick={() => openRow(inv)}
                          >
                            <Table.Td
                              className={`${listClasses.colCheck} ${sharedListClasses.stickyCheck}`}
                              onClick={(e) => e.stopPropagation()}
                              // Shift-click selects a range; keep it from also selecting page text.
                              onMouseDown={(e) => e.shiftKey && e.preventDefault()}
                            >
                              <Checkbox
                                color="brand"
                                size="sm"
                                radius="sm"
                                checked={selected}
                                onChange={(e) => selection.toggle(i, (e.nativeEvent as MouseEvent).shiftKey)}
                              />
                            </Table.Td>
                            <Table.Td
                              className={`${listClasses.colActions} ${listClasses.colActionsSingle} ${sharedListClasses.stickyActions} ${sharedListClasses.stickyEdge}`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <RowActions
                                inlineOnly
                                items={[
                                  {
                                    key: "pdf",
                                    intent: "pdf",
                                    icon: <DownloadSimpleIcon size={17} />,
                                    label: t("invoice:tooltip.pdf"),
                                    onClick: () => downloadPdf(`${inv.id}`),
                                    loading: isPdfLoading(inv.id),
                                    disabled: !INV_PDF.hasAccess,
                                    disabledReason: INV_PDF.reason,
                                  },
                                ]}
                              />
                            </Table.Td>
                            <Table.Td>
                              <PartyCell
                                name={name}
                                logo={inv?.receiverBusiness?.logo}
                                code={partySubline(inv)}
                                codeHref={rowHref(inv)}
                              />
                            </Table.Td>
                            <Table.Td>
                              <StatusBadge
                                color={resolveInvoiceStatusColor(statusCode)}
                                label={label}
                              />
                            </Table.Td>
                            <Table.Td>
                              <DueCell dueDate={inv?.paymentDate} paid={isPaid} />
                            </Table.Td>
                            <Table.Td className={listClasses.colNum}>
                              <MoneyCell amount={displayAmt} />
                            </Table.Td>
                            <Table.Td className={listClasses.colPayment}>
                              <PaymentProgressCell
                                paid={paidAmt}
                                total={displayAmt ?? 0}
                              />
                            </Table.Td>
                            <Table.Td className={listClasses.colNum}>
                              <MoneyCell
                                amount={remainingAmt > 0 ? remainingAmt : null}
                                emphasis
                              />
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
              </Table.Tbody>
            </Table>
            </Table.ScrollContainer>
            )}
          </Box>
        )}

        <ListFooter
          from={Math.min(total, (offset.page - 1) * offset.limit + 1)}
          to={Math.min(total, (offset.page - 1) * offset.limit + rows.length)}
          total={total}
          page={offset.page}
          pageSize={offset.limit}
          onPageChange={(p) => patchQuery({ page: p }, false)}
          onPageSizeChange={(limit) => patchQuery({ page: 1, limit })}
        />
      </Box>

      <BulkActionBar
        selectedCount={selection.count}
        remainingTotal={selectedRemaining}
        onClear={clearSelection}
        selectAllAction={
          canSelectAll
            ? {
                label: t(total > SELECT_ALL_CAP ? "common:list.bulkSelectFirst" : "common:list.bulkSelectAll", {
                  count: selectAllCount,
                }),
                onClick: handleSelectAll,
                loading: bulkBusy === "selectAll",
              }
            : undefined
        }
        primaryAction={
          INV_SETT_SEND.hasAccess
            ? {
                label: t("invoice:actions.sendReminder"),
                onClick: handleBulkReminder,
                loading: bulkBusy === "reminder",
              }
            : undefined
        }
        secondaryAction={
          INV_PDF.hasAccess
            ? {
                label: t("invoice:actions.downloadPdf"),
                onClick: handleBulkPdf,
                loading: bulkBusy === "pdf",
              }
            : undefined
        }
      />

      <Drawer
        size="lg"
        opened={action[0] === "payment_register"}
        onClose={() => setAction([])}
        withCloseButton={false}
        title={false}
        classNames={{ body: modalChrome.drawerBodyFlush }}
        styles={REGISTER_PAYMENT_DRAWER_STYLES}
      >
        <RegisterPayment
          reload={async () => {
            await mutate();
            tableRef.current?.reload?.();
          }}
          action={action}
          setAction={setAction}
        />
      </Drawer>
    </Page>
  );
};
